// React
import { useEffect, useState } from "react";

// Axios (for cachebusting)
import axios from "axios";

// Third Party Libraries
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { Alert, Container } from "@mui/material";
import { ConfirmProvider } from "material-ui-confirm";

// Project Configs
import { loginRequest } from './config/authConfig';
import { AppConfig } from "./config/appConfig";

// Project Routing
import Router from "./router";

// Project Types
import { IEmployee, IEmployeePermissions } from "./types/employee";
import { ICustomerCategoryOption, IListDictionary, ISelectOption, ISelectOptionWithEntity } from "./types/list";
import { IContainer } from "./types/container";
import { IExpense } from "./types/expense";
import { IReportRegistration } from "./types/report";

// Project Contexts
import { UserContext } from "./contexts/userContext";
import { ListContext } from "./contexts/listContext";

// Project Services
import { AuthService } from './services/authService';
import { EmployeeService } from "./services/employeeService";
import { ListService } from "./services/listService";
import { UtilityService } from "./services/utilityService";

// Project Components
import { SignInButton } from "./components/signInButton";
import Spinner from "./components/spinner";

// Project Helper Classes
import { CredentialManager } from "./classes/credentialManager";
import { UpdateManager } from "./classes/updateManager";

// Project Assets (images etc)
import Image_Logo from "./assets/Logo.jpg";

// Project CSS
import "./styles/app.scss";

// Hook all Axios requests to signal user interaction for update manager
axios.interceptors.request.use((config) => {
	UpdateManager.poke();
	return config;
});

function App() {
	const [contextUser, setContextUser] = useState<IEmployee | undefined>(undefined);
	const [listDictionary, setListDictionary] = useState<IListDictionary | undefined>();
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const msalContext = useMsal();
	const { instance: msalInstance, accounts: msalAccounts } = msalContext;

	CredentialManager.setMsalContext(msalContext);
	const [credentialManager, setCredentialManager] = useState<CredentialManager>();
	const [isCredentialManagerInitialized, setIsCredentialManagerInitialized] = useState<boolean>(false);
	const [updateManager, setUpdateManager] = useState<UpdateManager>();

	useEffect(() => {
		const _credentialManager = CredentialManager.getInstance();
		_credentialManager.onInitialized = () => { setIsCredentialManagerInitialized(true) };
		_credentialManager.initialize();
		setCredentialManager(_credentialManager);

		const _updateManager = UpdateManager.getInstance();
		_updateManager.onUpdateDetectedSubscribe(() => { window.location.reload(); });
		setUpdateManager(_updateManager);
	}, []);

	useEffect(() => {
		// Initialize user context
		if (!contextUser && msalAccounts?.length > 0 && msalAccounts[0].username && isCredentialManagerInitialized) {
			setTimeout(async () => {
				await loadUserProfile();

			}, 1000); //Fixes an error where the msal component isn't ready yet
		}
	}, [msalAccounts, isCredentialManagerInitialized])

	useEffect(() => {
		if (msalAccounts?.length > 0 && credentialManager?.ready && !credentialManager?.hasValidCredentials) {
			credentialManager?.forceRefresh();
		}
	}, [msalAccounts, credentialManager])


	const loadUserProfile = async () => {
		setErrorMessage(undefined);
		const newUser = cloneContextUser();

		const graphTokenResponse = await msalInstance.acquireTokenSilent({ ...loginRequest, account: msalAccounts[0], });
		const graphResponse = await AuthService.getGraphProfile(graphTokenResponse.accessToken);
		newUser.email = graphResponse["mail"];

		EmployeeService.getCurrentUser()
			.then((response) => {
				console.log('EmployeeService.getCurrentUser: SUCCESS', response);
				newUser.permissions = response.data.permissions as IEmployeePermissions;

				if (!newUser.permissions?.canAccessHwiOnline) {
					console.log('App.loadUserProfile: NOT AUTHORIZED TO USE APPLICATION, LOGGING OUT');
					credentialManager.clearCredentials();
					msalInstance.logoutRedirect({ account: msalInstance.getActiveAccount(), postLogoutRedirectUri: '/' });
				}

				newUser.id = response.data.id;
				newUser.details = response.data.details;
				newUser.tilsAccess = response.data.tilsAccess;
				newUser.tilsAccess?.sort((a: { customerName: string | undefined; }, b: { customerName: string | undefined; }) => UtilityService.compareStrings(a.customerName, b.customerName));
				msalInstance.setActiveAccount(msalAccounts[0]);
				setContextUser(newUser);
				loadLists();
			})
			.catch((error) => {
				console.log('EmployeeService.getCurrentUser: ERROR', error);

				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
			});
	}

	const cloneContextUser = () => {
		let newUser: IEmployee = {
			id: "",
			email: msalAccounts[0].username,
			displayName: msalAccounts[0].name,
		};

		if (contextUser) {
			newUser = JSON.parse(JSON.stringify(contextUser));
		}

		return newUser;
	}

	const loadLists = async () => {
		ListService.getSelectOptions()
			.then((response) => {
				console.log('ListService.getSelectOptions: SUCCESS', response);

				const b: ISelectOption[] = ListService.mapBrandOptions(response.data.brands);
				const cc: ICustomerCategoryOption[] = [];
				const cgs = ListService.mapCustomerGeneratorStatusOptions(response.data.customerGeneratorStatuses);
				const co: IContainer[] = response.data.containers;
				const cs = ListService.mapCustomerStatusOptions(response.data.customerStatuses);
				const dt: ISelectOptionWithEntity[] = ListService.mapDocumentTypeOptions(response.data.documentTypes);
				const e: ISelectOption[] = ListService.mapEmployeeOptions(response.data.employees);
				const ent: ISelectOption[] = ListService.mapEntityTypeOptions(response.data.entityTypes);
				const ext: IExpense[] = ListService.mapExpenseOptions(response.data.expenseTypes);
				const gs: ISelectOption[] = [{ name: "Active", value: "0" }, { name: "Inactive", value: "1" }]
				const m: ISelectOption[] = ListService.mapMaterialOptions(response.data.materials);
				const rr: IReportRegistration[] = ListService.mapReportRegistrations(response.data.reportRegistrations);
				const s = ListService.mapStateOptions(response.data.states);
				const sh: ISelectOption[] = [];
				const t: ISelectOption[] = ListService.mapTransporterOptions(response.data.transporters);
				const tt: ISelectOption[] = ListService.mapTrailerTypeOptions(response.data.trailerTypes);
				const uom: ISelectOption[] = ListService.mapUnitsOfMeasurement(response.data.unitsOfMeasure);
				const vm = ListService.mapVendorMethodOptions(response.data.vendorMethods);
				const vs = ListService.mapVendorStatusOptions(response.data.vendorStatuses);

				

				const lists: IListDictionary = {
					brands: b,
					containers: co,
					customerCategories: cc,
					customerStatuses: cs,
					customerGeneratorStatuses: cgs,
					documentTypes: dt,
					employees: e,
					entityTypes: ent,
					expenses: ext,
					generalStatuses: gs,
					materials: m,
					reportRegistrations: rr,
					states: s,
					shipments: sh,
					trailerTypes: tt,
					transporters: t,
					unitsOfMeasurement: uom,
					vendorMethods: vm,
					vendorStatuses: vs,

					updateListDictionary: (dictionary: IListDictionary) => {
						setListDictionary(dictionary);
					}
				};

				setListDictionary(lists);
			})
			.catch((error) => {
				console.log('ListService.getSelectOptions: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			});
	}

	const ready = () => {
		let ready = true;

		if (contextUser == undefined) ready = false;
		if (listDictionary == undefined) ready = false;
		if (credentialManager?.ready != true) ready = false;

		return ready;
	}

	return (
		<div className="App">
			<AuthenticatedTemplate>
				<Spinner dependency={!ready() && !errorMessage} fullHeight={true} text="Loading...">
					{ready() &&
						<ListContext.Provider value={listDictionary}>
							<UserContext.Provider value={contextUser}>
								<ConfirmProvider>
									<Router></Router>
								</ConfirmProvider>
							</UserContext.Provider>
						</ListContext.Provider>
					}
					{errorMessage &&
						<div className="api-error-template">
							<div className="title">
								<img src={Image_Logo}></img>
								<span>HWI Online</span>
							</div>
							<Alert className="alert-error alert-large"
								severity="error">
								{errorMessage}
							</Alert>
						</div>
					}
				</Spinner>
			</AuthenticatedTemplate>

			<UnauthenticatedTemplate>
				<Container maxWidth="lg" className="route-container">
					<div className="unauthenticated-template">
						<div className="title">
							<img src={Image_Logo}></img>
							<span>HWI Online</span>
						</div>
						<div className="content">
							You are currently unauthenticated. Please sign-in.
						</div>
						<div className="actions">
							<SignInButton></SignInButton>
						</div>
					</div>
				</Container>
			</UnauthenticatedTemplate>
		</div>
	);
}

export default App;
